import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  Alert,
  Col,
  Dropdown,
  Icon,
  Menu,
  Modal,
  Row,
  Spin,
  Table,
  Tooltip,
  notification,
} from "antd";

import ButtonStandard from "../../../components/utils/button";
import MenuIcon from "../../../components/utils/table/icons/menu";
import { Container as MenuContainer } from "../../../components/utils/table/menu/styles";
import TagStatus from "../../../components/utils/tagStatus";
import DrawerProductionBatch from "../../../components/drawers/productionBatch";
import DrawerProductionOrderDetails from "../../../components/drawers/productionOrderDetails";
import { TableActions } from "./styles";
import { numberMask } from "../../../services/helpersMethodsService";
import {
  closeProductionOrderPartial,
  indexProductionBatches,
  indexProductionOrders,
  showProductionOrder,
} from "../../../services/productionOrderService";
import { processReportProductionOrderSheet } from "../../../services/reportService";
import {
  resolveProductionOrderBlobError,
  resolveProductionOrderError,
} from "../../../utils/productionOrderErrors";

const Column = Table.Column;

const PRODUCTION_STATUSES = [
  "Scheduled",
  "InProgress",
  "ClosedTotal",
  "ClosedPartial",
];

const STATUS_COLORS = {
  Scheduled: {
    background: "#E4F1FF",
    borderColor: "#4A85AE",
    color: "#4A85AE",
  },
  InProgress: {
    background: "#F6FFC8",
    borderColor: "#A9C133",
    color: "#A9C133",
  },
  ClosedTotal: {
    background: "#C5F1CA",
    borderColor: "#106518",
    color: "#106518",
  },
  ClosedPartial: {
    background: "#FFDBBC",
    borderColor: "#FE8D2A",
    color: "#FE8D2A",
  },
};

const ProductionTable = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [batchesByOrder, setBatchesByOrder] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [alertDismissed, setAlertDismissed] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [orderSelected, setOrderSelected] = useState(null);
  const [batchNumber, setBatchNumber] = useState(null);
  const [detailsOrderId, setDetailsOrderId] = useState(null);

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const responses = await Promise.all(
        PRODUCTION_STATUSES.map((status) =>
          indexProductionOrders({ groupId, farmId, status }),
        ),
      );
      const all = responses.reduce(
        (accumulator, response) => [
          ...accumulator,
          ...(response?.data?.results ?? []),
        ],
        [],
      );
      all.sort((a, b) => (b.sequenceNumber ?? 0) - (a.sequenceNumber ?? 0));
      setOrders(all);
    } catch (error) {
      console.error(error);
      notification.error({
        message: translation.production.messages.errorLoad,
      });
    } finally {
      setIsLoading(false);
    }
  }, [groupId, farmId, translation]);

  useEffect(() => {
    if (groupId && farmId) {
      fetchData();
    }
  }, [groupId, farmId, fetchData]);

  const fetchBatches = useCallback(
    async (orderId) => {
      try {
        const {
          data: { results },
        } = await indexProductionBatches({ groupId, farmId, id: orderId });
        setBatchesByOrder((previous) => ({
          ...previous,
          [orderId]: results ?? [],
        }));
      } catch (error) {
        console.error(error);
        setBatchesByOrder((previous) => ({ ...previous, [orderId]: [] }));
        notification.error({
          message: translation.production.messages.errorLoad,
        });
      }
    },
    [groupId, farmId, translation],
  );

  const openExecuteDrawer = useCallback(
    async (record) => {
      setIsLoading(true);
      try {
        const {
          data: { results },
        } = await showProductionOrder({ groupId, farmId, id: record.id });
        setOrderSelected(results);
        setBatchNumber((record.executedBatches ?? 0) + 1);
        setShowDrawer(true);
      } catch (error) {
        notification.error({
          message: translation.production.messages.errorLoad,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [groupId, farmId, translation],
  );

  const closeDrawer = useCallback(() => {
    setShowDrawer(false);
    setOrderSelected(null);
    setBatchNumber(null);
  }, []);

  const handleExecuted = useCallback(async () => {
    const orderId = orderSelected?.id;
    closeDrawer();
    await fetchData();
    if (orderId) {
      await fetchBatches(orderId);
    }
  }, [closeDrawer, fetchBatches, fetchData, orderSelected]);

  const handleClosePartial = useCallback(
    (record) => {
      Modal.confirm({
        centered: true,
        title: translation.production.messages.confirmClosePartial,
        content: translation.production.messages.confirmClosePartialDetail,
        okText: translation.buttons.yes,
        cancelText: translation.buttons.no,
        onCancel() {},
        onOk: async () => {
          setIsLoading(true);
          try {
            await closeProductionOrderPartial({
              groupId,
              farmId,
              id: record.id,
            });
            notification.success({
              message: translation.production.messages.successClosePartial,
            });
            await fetchData();
          } catch (error) {
            notification.error({
              message: resolveProductionOrderError(
                error,
                translation,
                translation.production.messages.errorClosePartial,
              ),
            });
          } finally {
            setIsLoading(false);
          }
        },
      });
    },
    [farmId, fetchData, groupId, translation],
  );

  const handlePrint = useCallback(
    async (explicitIds) => {
      const ids =
        explicitIds != null && explicitIds.length > 0
          ? explicitIds
          : selectedIds;

      if (ids.length === 0) {
        notification.warning({
          message: translation.production.messages.nothingToPrint,
        });
        return;
      }

      setIsLoading(true);
      try {
        const { data } = await processReportProductionOrderSheet({
          groupId,
          farmId,
          body: { productionOrderIds: ids },
        });

        const downloadUrl = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute(
          "download",
          `Ficha_de_Producao_${moment().format("YYYYMMDD_HHmm")}.pdf`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        notification.error({
          message: await resolveProductionOrderBlobError(
            error,
            translation,
            translation.production.messages.errorPrint,
          ),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [farmId, groupId, selectedIds, translation],
  );

  const menu = (record) => (
    <Menu>
      {(record.status === "Scheduled" || record.status === "InProgress") && (
        <Menu.Item key="execute" onClick={() => openExecuteDrawer(record)}>
          {translation.production.menu.executeBatch}
        </Menu.Item>
      )}
      {record.status === "InProgress" && (
        <Menu.Item
          key="closePartial"
          onClick={() => handleClosePartial(record)}
        >
          {translation.production.menu.closePartial}
        </Menu.Item>
      )}
      <Menu.Item key="details" onClick={() => setDetailsOrderId(record.id)}>
        {translation.table.menu.details}
      </Menu.Item>
      <Menu.Item key="print" onClick={() => handlePrint([record.id])}>
        {translation.production.menu.print}
      </Menu.Item>
    </Menu>
  );

  const ingredientRow = (batch) => {
    const ingredients = batch.ingredients ?? [];

    if (ingredients.length === 0) {
      return (
        <span style={{ color: "#9074bf" }}>
          {translation.production.noIngredients}
        </span>
      );
    }

    return (
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={ingredients}
        style={{ width: "100%" }}
      >
        <Column
          title={translation.production.ingredientColumns.name}
          dataIndex="supplementName"
          align="left"
        />
        <Column
          title={translation.production.ingredientColumns.planned}
          dataIndex="quantityPlanned"
          align="right"
          render={(value) => numberMask(value)}
        />
        <Column
          title={translation.production.ingredientColumns.realized}
          dataIndex="quantityRealized"
          align="right"
          render={(value) => numberMask(value)}
        />
        <Column
          title={translation.production.ingredientColumns.deviation}
          dataIndex="deviationPercent"
          align="right"
          render={(value) =>
            value == null ? (
              <span style={{ color: "#9074bf" }}>—</span>
            ) : (
              `${numberMask(value)}%`
            )
          }
        />
      </Table>
    );
  };

  const expandedRow = (record) => {
    const batches = batchesByOrder[record.id];

    if (batches === undefined) {
      return (
        <Spin size="small">
          <span style={{ color: "#9074bf" }}>
            {translation.production.loadingBatches}
          </span>
        </Spin>
      );
    }

    if (batches.length === 0) {
      return (
        <span style={{ color: "#9074bf" }}>
          {translation.production.noBatchesYet}
        </span>
      );
    }

    return (
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={batches}
        style={{ width: "100%" }}
        expandedRowRender={ingredientRow}
      >
        <Column
          title={translation.production.batchColumns.number}
          dataIndex="batchNumber"
          align="left"
        />
        <Column
          title={translation.production.batchColumns.date}
          dataIndex="manufactureDatetime"
          align="left"
          render={(value) => (value ? moment(value).format("DD/MM/YYYY") : "-")}
        />
        <Column
          title={translation.production.batchColumns.plannedOriginalMatter}
          dataIndex="quantityPlannedOriginalMatterKg"
          align="right"
          render={(value) =>
            value == null ? (
              <span style={{ color: "#9074bf" }}>—</span>
            ) : (
              numberMask(value)
            )
          }
        />
        <Column
          title={translation.production.batchColumns.produced}
          dataIndex="quantityManufactured"
          align="right"
          render={(value) => numberMask(value)}
        />
        <Column
          title={translation.production.batchColumns.producedDryMatter}
          dataIndex="quantityManufacturedDryMatter"
          align="right"
          render={(value) => numberMask(value)}
        />
        <Column
          title={translation.production.batchColumns.deviation}
          dataIndex="deviationPercent"
          align="right"
          render={(value) =>
            value == null ? (
              <span style={{ color: "#9074bf" }}>—</span>
            ) : (
              `${numberMask(value)}%`
            )
          }
        />
      </Table>
    );
  };

  const missingStock = orders.filter((order) => order.limitedByStock);
  const missingStockCodes = missingStock.map((order) => order.code).join(", ");

  return (
    <>
      {missingStock.length > 0 && alertDismissed !== missingStockCodes && (
        <Row style={{ marginBottom: 12 }}>
          <Alert
            type="warning"
            showIcon
            closable
            onClose={() => setAlertDismissed(missingStockCodes)}
            message={translation.production.missingStockAlert.replace(
              "{{codes}}",
              missingStockCodes,
            )}
          />
        </Row>
      )}
      <Row type="flex" justify="end" gutter={8} style={{ marginBottom: 16 }}>
        <Col>
          <ButtonStandard
            buttonType="typeWithoutBackground"
            onClick={fetchData}
          >
            <Icon type="reload" />
          </ButtonStandard>
        </Col>
        <Col>
          <ButtonStandard
            type="button"
            buttonType="principal"
            onClick={handlePrint}
          >
            {translation.production.buttonPrint}
          </ButtonStandard>
        </Col>
      </Row>
      <Table
        loading={isLoading}
        rowKey="id"
        size="small"
        dataSource={orders}
        scroll={{ x: true }}
        style={{ width: "100%", margin: "0" }}
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: (keys) => setSelectedIds(keys),
        }}
        expandedRowRender={expandedRow}
        onExpand={(expanded, record) => {
          if (expanded) {
            fetchBatches(record.id);
          }
        }}
      >
        <Column
          title={translation.productionOrder.table.columns.status}
          dataIndex="status"
          key="status"
          align="left"
          render={(status) => (
            <TagStatus {...(STATUS_COLORS[status] ?? STATUS_COLORS.Scheduled)}>
              {translation.productionOrder.status[status] ?? status}
            </TagStatus>
          )}
        />
        <Column
          title={translation.productionOrder.table.columns.code}
          dataIndex="code"
          key="code"
          align="left"
        />
        <Column
          title={translation.productionOrder.table.columns.supplementName}
          dataIndex="supplementName"
          key="supplementName"
          align="left"
        />
        <Column
          title={translation.productionOrder.table.columns.productionDate}
          dataIndex="productionDate"
          key="productionDate"
          align="left"
          render={(value) => moment(value).format("DD/MM/YYYY")}
        />
        <Column
          title={translation.production.columns.batches}
          key="batches"
          align="right"
          render={(text, record) =>
            `${record.executedBatches ?? 0} / ${record.numberOfBatches ?? 0}`
          }
        />
        <Column
          title={translation.production.columns.plannedDryMatter}
          dataIndex="plannedDryMatterKg"
          key="plannedDryMatterKg"
          align="right"
          render={(value) => numberMask(value)}
        />
        <Column
          title={translation.production.columns.producedDryMatter}
          dataIndex="producedDryMatterKg"
          key="producedDryMatterKg"
          align="right"
          render={(value) => numberMask(value)}
        />
        <Column
          title={translation.production.columns.realizedTotalCost}
          dataIndex="realizedTotalCost"
          key="realizedTotalCost"
          align="right"
          render={(value) => numberMask(value, true)}
        />
        <Column
          align="left"
          render={(text, record) => (
            <TableActions>
              {(record.status === "Scheduled" ||
                record.status === "InProgress") && (
                <Tooltip title={translation.production.menu.executeBatch}>
                  <Icon
                    type="play-circle"
                    className="quickAction"
                    onClick={() => openExecuteDrawer(record)}
                  />
                </Tooltip>
              )}
              <Dropdown
                overlay={menu(record)}
                trigger={["click"]}
                key={record.id}
              >
                <MenuContainer>
                  <MenuIcon />
                </MenuContainer>
              </Dropdown>
            </TableActions>
          )}
        />
      </Table>
      <DrawerProductionBatch
        visible={showDrawer}
        order={orderSelected}
        batchNumber={batchNumber}
        onClose={closeDrawer}
        onSave={handleExecuted}
      />
      <DrawerProductionOrderDetails
        visible={detailsOrderId != null}
        orderId={detailsOrderId}
        onClose={() => setDetailsOrderId(null)}
      />
    </>
  );
};

export default ProductionTable;
